import React, { useState, useEffect } from 'react';
import { Smartphone, TrendingUp, DollarSign, Users, Percent, Download, ShoppingCart } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const PWAAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/pwa-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching PWA analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        <p className="mt-4 text-gray-400">Loading PWA analytics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-8 text-center text-gray-400">
        <p>Unable to load PWA analytics.</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total App Installs',
      value: stats.total_installs,
      icon: <Download className="text-blue-400" size={24} />,
      color: 'blue',
      subtitle: `${stats.recent_installs_30d} in last 30 days`
    },
    {
      title: 'App Bookings',
      value: stats.pwa_bookings,
      icon: <ShoppingCart className="text-green-400" size={24} />,
      color: 'green',
      subtitle: 'Bookings through app'
    },
    {
      title: 'Conversion Rate',
      value: `${stats.conversion_rate}%`,
      icon: <Percent className="text-purple-400" size={24} />,
      color: 'purple',
      subtitle: 'Installs → Bookings'
    },
    {
      title: 'Total App Revenue',
      value: `$${stats.total_pwa_revenue.toFixed(2)}`,
      icon: <DollarSign className="text-emerald-400" size={24} />,
      color: 'emerald',
      subtitle: `Avg: $${stats.average_booking_value.toFixed(2)}/booking`
    },
    {
      title: 'Discounts Given',
      value: `$${stats.total_discounts_given.toFixed(2)}`,
      icon: <TrendingUp className="text-orange-400" size={24} />,
      color: 'orange',
      subtitle: '10% first booking discount'
    },
    {
      title: 'Pending Discounts',
      value: stats.discount_available,
      icon: <Users className="text-pink-400" size={24} />,
      color: 'pink',
      subtitle: 'Users with unused discount'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Smartphone className="text-purple-400" size={32} />
          <div>
            <h2 className="text-2xl font-bold text-white">PWA Analytics</h2>
            <p className="text-gray-400 text-sm">HQSTUDIO+ App Performance</p>
          </div>
        </div>
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br from-gray-900 to-gray-800 border border-${stat.color}-500/20 rounded-xl p-6 hover:border-${stat.color}-500/50 transition-all`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 bg-${stat.color}-500/10 rounded-lg`}>
                {stat.icon}
              </div>
            </div>
            <h3 className="text-gray-400 text-sm font-medium mb-2">{stat.title}</h3>
            <p className="text-3xl font-bold text-white mb-2">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.subtitle}</p>
          </div>
        ))}
      </div>

      {/* Additional Insights */}
      <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp size={20} className="text-purple-400" />
          Key Insights
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between py-2 border-b border-purple-500/20">
            <span className="text-gray-300">App Install to Booking Rate</span>
            <span className="text-white font-semibold">{stats.conversion_rate}%</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-purple-500/20">
            <span className="text-gray-300">Discount Redemption Rate</span>
            <span className="text-white font-semibold">
              {stats.total_installs > 0 ? ((stats.discount_used / stats.total_installs) * 100).toFixed(1) : 0}%
            </span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-purple-500/20">
            <span className="text-gray-300">Revenue per Install</span>
            <span className="text-white font-semibold">
              ${stats.total_installs > 0 ? (stats.total_pwa_revenue / stats.total_installs).toFixed(2) : '0.00'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-300">Active Installs (Unused Discount)</span>
            <span className="text-white font-semibold">{stats.discount_available}</span>
          </div>
        </div>
      </div>

      {/* Pro Tip */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
        <p className="text-sm text-blue-200">
          💡 <strong>Pro Tip:</strong> Track app installs and bookings in Google Analytics for detailed user behavior insights. 
          The conversion rate shows how many app installers become paying customers.
        </p>
      </div>
    </div>
  );
};

export default PWAAnalytics;
